import React, { useState, useEffect, useCallback } from 'react';
import 'react-toastify/dist/ReactToastify.css';
import './SearchBar.css';
import { CountyData, Providers, ProviderAttributes, InsuranceData } from '../Utility/Types';
import { fetchCountiesByState, fetchStates, fetchPracticeTypes, PracticeType } from '../Utility/ApiCall';
import { ChevronDown, ChevronUp, Search, RotateCcw, MapPin } from 'lucide-react';

interface SearchBarProps {
  onResults: (results: Providers) => void;
  onSearch: (params: {
    query: string;
    county_name: string;
    insurance: string;
    spanish: string;
    service: string;
    waitlist: string;
    age: string;
    providerType: string;
    stateId: string;
    state: string;
    hasReviews: string;
  }) => void;
  onCountyChange: (county_name: string) => void;
  insuranceOptions: InsuranceData[];
  onInsuranceChange: (insurance: string) => void;
  onSpanishChange: (spanish: string) => void;
  onServiceChange: (service: string) => void;
  onWaitListChange: (waitlist: string) => void;
  onAgeChange: (age: string) => void;
  onReviewsChange: (hasReviews: string) => void;
  onReset: () => void;
  providers: ProviderAttributes[];
  onProviderTypeChange: (providerType: string) => void;
  onStateChange?: (stateId: string) => void;
  totalProviders: number;
  showSearchNotification?: boolean;
  nearMeFilter?: { zip: string; radiusMiles: number } | null;
  onNearMeSearch?: (zip: string, radiusMiles: number) => void;
  onClearNearMe?: () => void;
}

const SearchBar: React.FC<SearchBarProps> = ({
  onSearch,
  onCountyChange,
  insuranceOptions = [],
  onInsuranceChange,
  onSpanishChange,
  onServiceChange,
  onWaitListChange,
  onAgeChange,
  onReviewsChange,
  onReset,
  providers = [],
  totalProviders,
  onProviderTypeChange,
  onStateChange,
  showSearchNotification = false,
  nearMeFilter = null,
  onNearMeSearch,
  onClearNearMe,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCounty, setSelectedCounty] = useState<string>('');
  const [selectedInsurance, setSelectedInsurance] = useState<string>('');
  const [selectedSpanish, setSelectedSpanish] = useState<string>('');
  const [selectedService, setSelectedService] = useState<string>('');
  const [selectedWaitList, setSelectedWaitList] = useState<string>('');
  const [selectedAge, setSelectedAge] = useState<string>('');
  const [selectedHasReviews, setSelectedHasReviews] = useState<string>('');
  const [showNotification, setShowNotification] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [selectedProviderType, setSelectedProviderType] = useState<string>('none');
  const [providerStates, setProviderStates] = useState<any[]>([]);
  const [selectedStateId, setSelectedStateId] = useState<string>('none');
  const [selectedState, setSelectedState] = useState<string>('none');
  const [counties, setCounties] = useState<CountyData[]>([]);
  const [advancedOptions, setAdvancedOptions] = useState(false);
  const [practiceTypes, setPracticeTypes] = useState<PracticeType[]>([]);
  const [nearZipInput, setNearZipInput] = useState("");
  const [nearRadiusMiles, setNearRadiusMiles] = useState(25);

  useEffect(() => {
    if (nearMeFilter) {
      setNearZipInput(nearMeFilter.zip);
      setNearRadiusMiles(nearMeFilter.radiusMiles);
    } else {
      setNearZipInput("");
      setNearRadiusMiles(25);
    }
  }, [nearMeFilter]);

  // Ensure props are always arrays to prevent runtime errors
  const safeProviders = Array.isArray(providers) ? providers : [];
  const safeInsuranceOptions = Array.isArray(insuranceOptions) ? insuranceOptions : [];
  const safeCounties = Array.isArray(counties) ? counties : [];
  const safeProviderStates = Array.isArray(providerStates) ? providerStates : [];

  // Validate props to ensure they are always arrays
  useEffect(() => {
    // Silent validation - no console output
  }, [providers, insuranceOptions, counties, providerStates]);

  // Fetch states on component mount
  useEffect(() => {
    const getStates = async () => {
      try {
        const statesData = await fetchStates();
        setProviderStates(statesData || []);
      } catch (error) {
        // Fallback state options if API fails
        const fallbackStates = [
          { id: 1, attributes: { name: 'Utah', abbreviation: 'UT' } },
          { id: 6, attributes: { name: 'California', abbreviation: 'CA' } },
          { id: 44, attributes: { name: 'Texas', abbreviation: 'TX' } },
          { id: 33, attributes: { name: 'New York', abbreviation: 'NY' } },
          { id: 10, attributes: { name: 'Florida', abbreviation: 'FL' } },
          { id: 36, attributes: { name: 'Ohio', abbreviation: 'OH' } },
          { id: 14, attributes: { name: 'Illinois', abbreviation: 'IL' } },
          { id: 34, attributes: { name: 'North Carolina', abbreviation: 'NC' } },
          { id: 39, attributes: { name: 'Pennsylvania', abbreviation: 'PA' } },
          { id: 23, attributes: { name: 'Michigan', abbreviation: 'MI' } }
        ];
        setProviderStates(fallbackStates);
      }
    };
    getStates();
  }, []);

  useEffect(() => {
    const loadPracticeTypes = async () => {
      try {
        const response = await fetchPracticeTypes();
        setPracticeTypes(response.data);
      } catch (error) {
      }
    };
    loadPracticeTypes();
  }, []);


  // Fetch counties whenever selected state changes
  useEffect(() => {
    const getCounties = async () => {
      if (selectedStateId !== 'none') {
        try {
          const countiesData = await fetchCountiesByState(parseInt(selectedStateId));
          setCounties(countiesData || []);
          // Reset county selection when state changes (counties list changed)
          setSelectedCounty('');
          onCountyChange('');
        } catch (err: any) {
          // 401 is expected for public users - counties endpoint requires auth
          // We can't reliably extract counties from provider data because:
          // 1. Providers may serve multiple states
          // 2. County.state field is often missing
          // 3. We can't verify which state a county belongs to without the API
          // So we'll set an empty array - county filtering won't be available for public users
          if (err?.message && !err.message.includes('401') && !err.message.includes('Unauthorized')) {
            console.error('Failed to load counties:', err);
          }
          
          // Set empty array - county filtering requires authentication
          setCounties([]);
          
          // Reset county selection when state changes and counties couldn't be loaded
          setSelectedCounty('');
          onCountyChange('');
        }
      } else {
        setCounties([]);
        setSelectedCounty('');
        onCountyChange('');
      }
    };

    getCounties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStateId]); // Only depend on selectedStateId, not onCountyChange

  useEffect(() => {
    if (showNotification) {
      const hideTimer = setTimeout(() => {
        setShowNotification(false);
      }, 3000);
      return () => clearTimeout(hideTimer);
    } else if (!showNotification && isVisible) {
      const removeTimer = setTimeout(() => {
        setIsVisible(false);
      }, 300);
      return () => clearTimeout(removeTimer);
    }
  }, [showNotification, isVisible]);

  const handleSearch = useCallback(() => {
    // Reset notification state
    setShowNotification(false);
    setIsVisible(false);
    
    onSearch({
      query: searchQuery,
      county_name: selectedCounty,
      insurance: selectedInsurance,
      spanish: selectedSpanish,
      service: selectedService,
      waitlist: selectedWaitList,
      age: selectedAge,
      providerType: selectedProviderType,
      stateId: selectedStateId,
      state: selectedState,
      hasReviews: selectedHasReviews,
    });
    
    // Don't show notification immediately - let the parent component control it
  }, [searchQuery, selectedCounty, selectedInsurance, selectedSpanish, selectedService, selectedWaitList, selectedAge, selectedProviderType, selectedStateId, selectedState, selectedHasReviews, onSearch]);

  // Show notification when parent component signals it's ready
  useEffect(() => {
    if (showSearchNotification) {
      setShowNotification(true);
      setIsVisible(true);
    }
  }, [showSearchNotification]);

  const handleReset = () => {
    setSearchQuery('');
    setSelectedCounty('');
    setSelectedInsurance('');
    setSelectedSpanish('');
    setSelectedService('');
    setSelectedWaitList('');
    setSelectedAge('');
    setSelectedHasReviews('');
    setSelectedProviderType('none');
    setSelectedStateId('none');
    onReset();
    onCountyChange('');
    onInsuranceChange('');
    onSpanishChange('');
    onServiceChange('');
    onWaitListChange('');
    onAgeChange('');
    onReviewsChange('');
    onProviderTypeChange('none');
    setSelectedState('none');
    onClearNearMe?.();
  };

  const handleNearMeClick = () => {
    if (onNearMeSearch) {
      onNearMeSearch(nearZipInput.trim(), nearRadiusMiles);
    }
  };

  const handleClearNearMeClick = () => {
    onClearNearMe?.();
  };

  const ageOptions = [
    { label: 'All Ages', value: '' },
    { label: '0-2 years', value: '0-2' },
    { label: '3-5 years', value: '3-5' },
    { label: '5-7 years', value: '5-7' },
    { label: '8-10 years', value: '8-10' },
    { label: '11-13 years', value: '11-13' },
    { label: '13-15 years', value: '13-15' },
    { label: '16-18 years', value: '16-18' },
    { label: '19+ years', value: '19+' },
  ];

  const providerTypeOptions = [
    { label: 'Select Type', value: 'none', id: 0 },
    ...practiceTypes.map((type) => ({
      label: type.name,
      value: type.name,
      id: type.id,
    })),
  ];

  return (
    <section className="provider-map-search-section">

      <div className="provider-map-searchbar">
        <div className="near-me-panel">
          <div className="near-me-panel-header">
            <MapPin className="near-me-icon" size={20} aria-hidden />
            <span className="near-me-title">Find providers near me</span>
            {nearMeFilter && (
              <span className="near-me-active-badge">Active</span>
            )}
          </div>
          <p className="near-me-help">
            Enter your ZIP code and how far you&apos;re willing to travel.
            Distance uses ZIP centroids (approximate).
          </p>
          <div className="near-me-controls">
            <label className="near-me-label">
              <span className="sr-only">ZIP code</span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="postal-code"
                maxLength={10}
                placeholder="ZIP code"
                className="near-me-zip-input"
                value={nearZipInput}
                onChange={(e) => setNearZipInput(e.target.value)}
                aria-label="ZIP code for near me search"
              />
            </label>
            <label className="near-me-label">
              <span className="sr-only">Radius in miles</span>
              <select
                className="near-me-radius-select"
                value={nearRadiusMiles}
                onChange={(e) =>
                  setNearRadiusMiles(Number(e.target.value))
                }
                aria-label="Search radius in miles"
              >
                <option value={5}>5 mi</option>
                <option value={10}>10 mi</option>
                <option value={25}>25 mi</option>
                <option value={50}>50 mi</option>
                <option value={100}>100 mi</option>
              </select>
            </label>
            <button
              type="button"
              className="near-me-search-btn"
              onClick={handleNearMeClick}
              disabled={!onNearMeSearch}
            >
              Search near me
            </button>
            {nearMeFilter && onClearNearMe && (
              <button
                type="button"
                className="near-me-clear-btn"
                onClick={handleClearNearMeClick}
              >
                Clear location
              </button>
            )}
          </div>
        </div>

        <div className="filter-container">
          <div className="filter-item provider-state-dropdown">
            <select
              className="provider-state-select"
              value={selectedStateId}
              onChange={(e) => {
                const stateId = e.target.value;
                setSelectedStateId(stateId);
                // Notify parent component of state change
                if (onStateChange) {
                  onStateChange(stateId);
                }
                if (stateId !== 'none') {
                  const state = providerStates.find(s => s.id.toString() === stateId);
                  setSelectedState(state?.attributes.abbreviation || 'none');
                } else {
                  setSelectedState('none');
                }
              }}
              aria-label="Select State"
            >
              <option value="none">All States</option>
              {safeProviderStates.length > 0 
                ? safeProviderStates.map((providerState) => (
                    <option 
                      key={providerState.id} 
                      value={providerState.id}
                    >
                      {providerState.attributes.name}
                    </option>
                  ))
                : null}
            </select>
          </div>

          <div className={`filter-item provider-type-dropdown ${selectedProviderType === 'none' ? 'focus' : ''}`}>
            <select
              className="provider-type-select"
              value={selectedProviderType}
              onChange={(e) => {
                const newValue = e.target.value;
                setSelectedProviderType(newValue);
                onProviderTypeChange(newValue);
              }}
              required
              aria-label="Select Provider Type"
            >
              {providerTypeOptions.map((option) => (
                <option key={option.id} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="search-group">
            <input
              type="text"
              placeholder="Search by name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              list="provider-names"
              className="max-w-48"
            />
            <datalist id="provider-names">
              {safeProviders.length > 0
                ? safeProviders.map((provider, index) => (
                    <option key={index} value={provider.name ?? ''} />
                  ))
                : null}
            </datalist>
          </div>

          <div className="filter-item provider-insurance-dropdown">
            <input
              type="text"
              className="provider-insurance-select"
              value={selectedInsurance}
              onChange={(e) => {
                setSelectedInsurance(e.target.value);
                onInsuranceChange(e.target.value);
              }}
              list="insurance-options"
              placeholder="Search by insurance"
              aria-label="Select Insurance"
            />
            <datalist id="insurance-options">
              <option value="">All Insurance</option>
              {safeInsuranceOptions.length > 0
                ? safeInsuranceOptions
                    .filter((insurance) => insurance && insurance.attributes && insurance.attributes.name !== 'Contact us')
                    .sort((a, b) => a.attributes.name.localeCompare(b.attributes.name))
                    .map((insurance, index) => (
                      <option key={index} value={insurance.attributes.name} />
                    ))
                : null}
            </datalist>
          </div>
        </div>

        {advancedOptions && (
          <div className="filter-group">
            <div className="filter-item provider-county-dropdown">
              <select
                className="provider-county-select"
                value={selectedCounty}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedCounty(value);
                  onCountyChange(value);
                }}
                aria-label="Select County"
                required
                disabled={selectedStateId === 'none' || selectedStateId === ''}
              >
                <option value="">All Counties ({safeCounties.length} available)</option>
                {safeCounties.length > 0
                  ? safeCounties.map((county) => (
                      <option key={county.id} value={county.attributes.name}>
                        {county.attributes.name}
                      </option>
                    ))
                  : null}
              </select>
            </div>

            <div className="filter-item provider-age-dropdown">
              <select
                className="provider-age-select"
                value={selectedAge}
                onChange={(e) => {
                  setSelectedAge(e.target.value);
                  onAgeChange(e.target.value);
                  // Don't trigger auto-search for age changes
                }}
                aria-label="Select Age Group"
              >
                {ageOptions.map((option, index) => (
                  <option key={index} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-item provider-service-dropdown">
              <select
                className="provider-service-select"
                value={selectedService}
                onChange={(e) => {
                  setSelectedService(e.target.value);
                  onServiceChange(e.target.value);
                }}
                aria-label="Select Service Type"
              >
                <option value="">All Services</option>
                <option value="in_home">In-Home Services</option>
                <option value="telehealth">Telehealth Services</option>
                <option value="in_clinic">In-Clinic Services</option>
                <option value="multiple">Multiple Service Types</option>
              </select>
            </div>

            <div className="filter-item provider-waitlist-dropdown">
              <select
                className="provider-waitlist-select"
                value={selectedWaitList}
                onChange={(e) => {
                  setSelectedWaitList(e.target.value);
                  onWaitListChange(e.target.value);
                }}
                aria-label="Select Waitlist Status"
              >
                <option value="">All Waitlist Status</option>
                <option value="Currently accepting clients">Currently accepting clients</option>
                <option value="Short waitlist">Short waitlist</option>
                <option value="Long waitlist">Long waitlist</option>
                <option value="Not accepting new clients">Not accepting new clients</option>
              </select>
            </div>

            <div className="filter-item provider-spanish-dropdown">
              <select
                className="provider-spanish-select"
                value={selectedSpanish}
                onChange={(e) => {
                  setSelectedSpanish(e.target.value);
                  onSpanishChange(e.target.value);
                }}
                aria-label="Spanish Language Option"
              >
                <option value="">Spanish?</option>
                <option value="yes">Yes</option>
              </select>
            </div>

            <div className="filter-item provider-reviews-dropdown">
              <select
                className="provider-reviews-select"
                value={selectedHasReviews}
                onChange={(e) => {
                  setSelectedHasReviews(e.target.value);
                  onReviewsChange(e.target.value);
                }}
                aria-label="Reviews Filter"
              >
                <option value="">All Providers</option>
                <option value="has_reviews">Has Google Reviews</option>
                <option value="no_reviews">No Google Reviews</option>
              </select>
              <small className="text-xs text-gray-500 mt-1 block">
                Filter by Google review availability
              </small>
            </div>
          </div>
        )}

        <div className="button-group">
          <button className="advanced-options-button" onClick={() => setAdvancedOptions(!advancedOptions)}>
            <span className="flex items-center gap-2">
              Advanced Options
              {advancedOptions ? <ChevronUp /> : <ChevronDown />}
            </span>
          </button>
          <button
            className="provider-search-button"
            onClick={handleSearch}
            disabled={selectedProviderType === 'none'}
          >
            <Search size={18} />Search
          </button>
          <button className="provider-reset-button" onClick={handleReset}>
            <RotateCcw size={18} />Reset
          </button>
        </div>
        
      </div>
      {isVisible && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className={`bg-steelblue text-white p-4 rounded-lg shadow-lg transform transition-all duration-300 ease-in-out ${showNotification ? 'animate-jump-in' : 'animate-jump-out'}`}>
            <span className="font-bold">
              {!providers || providers.length === 0
                ? `No ${selectedProviderType} providers found. Try adjusting your filters or check back later for new providers.`
                : `Your search resulted in ${providers.length} ${selectedProviderType} providers`}
            </span>
          </div>
        </div>
      )}
    </section>
  );
};

export default SearchBar;